import { uniqBy } from 'lodash'
import { useState } from 'react'
import { Icon20, Icon24 } from 'cio-design-library/icons'

import { CareerProfileData } from 'builder/modules/careerProfile'
import { FormInput } from 'builder/components/CareerPath2Questionnaire'
import { AutoSuggestContainer } from 'builder/views/CareerProfile/TargetRoleDialog/styles'
import {
  RemoveIcon,
  SelectedListItem,
  TargetRoleTitle,
} from 'builder/views/CareerProfile/components/CareerProfileEditorView/styles'

import { useI18n } from 'builder/hooks/useI18n'
import { useMediaQueries } from 'builder/hooks/useMediaQueries'

import * as Styles from './styles'

const TRANSLATION = 'builder.dashboard.learning_hub.job_title_modal'

type Props = {
  onClose: () => void
  onSubmit: () => void
  isProfileUpdating: boolean
  localProfile: CareerProfileData | null
  onUpdate: <T extends keyof CareerProfileData>(fieldName: T, value: CareerProfileData[T]) => void
}

export const JobTitleForm = ({
  localProfile,
  onClose,
  onSubmit,
  onUpdate,
  isProfileUpdating,
}: Props) => {
  const { i18n } = useI18n()
  const { isPhone } = useMediaQueries()

  const [isExpanded, setIsExpanded] = useState(false)
  const [targetRoleText, setTargetRoleText] = useState('')

  const onRemoveTargetRole = (standardTitle: string) => {
    if (!localProfile) return
    onUpdate(
      'targetRoles',
      localProfile.targetRoles.filter(el => el.standardTitle !== standardTitle),
    )
  }

  const onUpdateTargetRole = (value: string) => {
    setTargetRoleText('')
    onUpdate(
      'targetRoles',
      uniqBy(
        [...(localProfile?.targetRoles || []), { id: null, standardTitle: value }],
        'standardTitle',
      ),
    )
  }

  return (
    <Styles.Container>
      <Styles.HeaderContainer>
        <Styles.InfoContainer>
          <Styles.InfoHeader>{i18n.t(`${TRANSLATION}.title`)}</Styles.InfoHeader>
          <Styles.InfoDescription>{i18n.t(`${TRANSLATION}.description`)}</Styles.InfoDescription>
        </Styles.InfoContainer>
        {!isPhone && (
          <Styles.CloseButton onClick={onClose}>
            <Styles.IconContainer>
              <Icon20.Close />
            </Styles.IconContainer>
          </Styles.CloseButton>
        )}
      </Styles.HeaderContainer>

      <Styles.JobTitleList>
        {localProfile?.targetRoles.map(targetRole => (
          <SelectedListItem key={targetRole.standardTitle}>
            <TargetRoleTitle>{targetRole.standardTitle}</TargetRoleTitle>
            <RemoveIcon onClick={() => onRemoveTargetRole(targetRole.standardTitle)} />
          </SelectedListItem>
        ))}
      </Styles.JobTitleList>

      <AutoSuggestContainer $isExpanded={isExpanded}>
        <FormInput
          onClear={() => setTargetRoleText('')}
          icon={<Icon24.Search />}
          placeholder={i18n.t(`${TRANSLATION}.search_placeholder`)}
          name="occupation"
          value={targetRoleText}
          suggestionParam="job-titles"
          storageKey="JOB_TITLE_HISTORY"
          isJobExpanded={isExpanded}
          showInputAsSuggestion
          multiSection={!targetRoleText.length}
          suggestionOptions={{ limit: 10 }}
          onExpand={setIsExpanded}
          onValueChange={value => {
            setTargetRoleText(value)
          }}
          onSelectAutosuggestion={value => {
            onUpdateTargetRole(value)
          }}
        />
      </AutoSuggestContainer>

      <Styles.ActionsContainer>
        <Styles.CancelButton onClick={onClose}>
          {i18n.t(`${TRANSLATION}.cancel`)}
        </Styles.CancelButton>
        <Styles.ConfirmButton onClick={onSubmit} isLoading={isProfileUpdating}>
          {i18n.t(`${TRANSLATION}.continue`)}
        </Styles.ConfirmButton>
      </Styles.ActionsContainer>
    </Styles.Container>
  )
}
